import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import type { PropertyValues } from 'lit';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';

type InputSize = 'sm' | 'md' | 'lg';
type InputType = 'numeric' | 'alphanumeric';

/**
 * A specialized input control for entering verification or security codes
 * (OTP, 2FA, PIN). Renders each digit as an individual box with automatic
 * focus advancement, paste distribution, and optional visual grouping with
 * a separator between groups.
 *
 * The component is presentational — verification logic lives in the consumer.
 * Participates in HTML forms via `ElementInternals`.
 *
 * @element asc-verification-code-input
 *
 * @fires asc-input    - Fired on any digit change. Detail: `{ value: string }`
 * @fires asc-complete - Fired when all boxes are filled. Detail: `{ value: string }`
 * @fires asc-submit   - Fired when all boxes are filled and `autoSubmit` is true. Detail: `{ value: string }`
 * @fires asc-focus    - Fired when a digit box gains focus. Detail: `{ index: number }`
 * @fires asc-blur     - Fired when the component loses focus entirely. Detail: `{ index: number }`
 *
 * @slot label - Required. Plain text label rendered above the digit boxes.
 * @slot hint  - Optional. Helper text rendered below the digit boxes. Hidden when `destructive` is true.
 * @slot error - Optional. Error message rendered below the boxes. Visible only when `destructive` is true.
 *
 * @csspart base      - Root flex column wrapper; target for overall layout overrides.
 * @csspart label-row - Wrapper around the label slot.
 * @csspart input-row - Flex row containing all digit boxes and separators.
 * @csspart box       - Outer styled box for each digit — border, bg, radius, shadow.
 * @csspart input     - The actual `<input>` element inside each box.
 * @csspart separator - Visual separator rendered between groups.
 * @csspart hint-row  - Wrapper around the hint or error slot.
 *
 * @cssprop --asc-verification-code-input-box-size-sm        - Width and height of each digit box at size="sm". Default: `64px`
 * @cssprop --asc-verification-code-input-box-size-md        - Width and height at size="md". Default: `80px`
 * @cssprop --asc-verification-code-input-box-size-lg        - Width and height at size="lg". Default: `96px`
 * @cssprop --asc-verification-code-input-box-radius-sm      - Border radius at size="sm". Default: `var(--asc-radius-lg)`
 * @cssprop --asc-verification-code-input-box-radius-md      - Border radius at size="md". Default: `var(--asc-radius-xl)`
 * @cssprop --asc-verification-code-input-box-radius-lg      - Border radius at size="lg". Default: `var(--asc-radius-md)`
 * @cssprop --asc-verification-code-input-gap-sm             - Space between boxes at size="sm". Default: `var(--asc-spacing-md)`
 * @cssprop --asc-verification-code-input-gap-md             - Space between boxes at size="md" and size="lg". Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-verification-code-input-box-bg             - Fill of each digit box. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-verification-code-input-box-bg-disabled    - Fill when disabled. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-verification-code-input-box-border         - Border color in default state. Default: `var(--asc-border-primary)`
 * @cssprop --asc-verification-code-input-box-border-focus   - Border color when focused. Default: `var(--asc-border-brand)`
 * @cssprop --asc-verification-code-input-box-border-destructive - Border color when destructive. Default: `var(--asc-border-error)`
 * @cssprop --asc-verification-code-input-box-border-disabled - Border color when disabled. Default: `var(--asc-border-disabled)`
 * @cssprop --asc-verification-code-input-box-shadow         - Drop shadow on each box. Default: `0px 1px 2px 0px var(--asc-shadow-xs)`
 * @cssprop --asc-verification-code-input-focus-ring         - Outer focus ring color. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-verification-code-input-digit-font-family  - Display font. Default: `var(--asc-font-family-display)`
 * @cssprop --asc-verification-code-input-digit-font-weight  - Medium weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-verification-code-input-digit-font-size-sm - Font size for size="sm" and size="md". Default: `var(--asc-font-size-display-lg)`
 * @cssprop --asc-verification-code-input-digit-font-size-lg - Font size for size="lg". Default: `var(--asc-font-size-display-xl)`
 * @cssprop --asc-verification-code-input-digit-line-height-sm - Line height for size="sm" and size="md". Default: `var(--asc-line-height-display-lg)`
 * @cssprop --asc-verification-code-input-digit-line-height-lg - Line height for size="lg". Default: `var(--asc-line-height-display-xl)`
 * @cssprop --asc-verification-code-input-digit-color-placeholder - Placeholder digit color. Default: `var(--asc-text-placeholder_subtle)`
 * @cssprop --asc-verification-code-input-digit-color        - Filled digit text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-verification-code-input-digit-color-disabled - Digit color when disabled. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-verification-code-input-label-color        - Label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-verification-code-input-label-font-size    - Label font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-verification-code-input-label-font-weight  - Label font weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-verification-code-input-hint-color         - Hint text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-verification-code-input-error-color        - Error text color. Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-verification-code-input-field-gap          - Vertical gap between label, input row, and hint/error. Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-verification-code-input-separator-color    - Color of the separator character between groups. Default: `var(--asc-text-placeholder_subtle)`
 * @cssprop --asc-verification-code-input-separator-font-family - Separator font family. Default: `var(--asc-font-family-display)`
 * @cssprop --asc-verification-code-input-separator-font-size - Separator font size. Matches digit font size for the active size.
 */
@customElement('asc-verification-code-input')
export class AscVerificationCodeInput extends LitElement {
  static formAssociated = true;

  static override styles = css`
    :host {
      display: inline-block;
    }

    :host([disabled]) {
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Base layout ─────────────────────────────────────────── */

    [part='base'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-verification-code-input-field-gap, var(--asc-spacing-sm));
    }

    /* ── Label row ───────────────────────────────────────────── */

    [part='label-row'] {
      display: block;
      color: var(--asc-verification-code-input-label-color, var(--asc-text-secondary));
      font-size: var(--asc-verification-code-input-label-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-verification-code-input-label-font-weight, var(--asc-font-weight-medium));
      line-height: var(--asc-line-height-text-sm);
      font-family: var(--asc-font-family-body);
    }

    /* ── Input row ───────────────────────────────────────────── */

    [part='input-row'] {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: nowrap;
      gap: var(--asc-verification-code-input-gap-md, var(--asc-spacing-lg));
    }

    :host([size='sm']) [part='input-row'] {
      gap: var(--asc-verification-code-input-gap-sm, var(--asc-spacing-md));
    }

    /* ── Box ─────────────────────────────────────────────────── */

    [part='box'] {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;
      background: var(--asc-verification-code-input-box-bg, var(--asc-bg-primary));
      border: 1px solid var(--asc-verification-code-input-box-border, var(--asc-border-primary));
      box-shadow: var(
        --asc-verification-code-input-box-shadow,
        0px 1px 2px 0px var(--asc-shadow-xs)
      );

      /* Default size: md */
      width: var(--asc-verification-code-input-box-size-md, 80px);
      height: var(--asc-verification-code-input-box-size-md, 80px);
      border-radius: var(--asc-verification-code-input-box-radius-md, var(--asc-radius-xl));
    }

    :host([size='sm']) [part='box'] {
      width: var(--asc-verification-code-input-box-size-sm, 64px);
      height: var(--asc-verification-code-input-box-size-sm, 64px);
      border-radius: var(--asc-verification-code-input-box-radius-sm, var(--asc-radius-lg));
    }

    :host([size='lg']) [part='box'] {
      width: var(--asc-verification-code-input-box-size-lg, 96px);
      height: var(--asc-verification-code-input-box-size-lg, 96px);
      border-radius: var(--asc-verification-code-input-box-radius-lg, var(--asc-radius-md));
    }

    /* Filled (has value, not focused) — 2px brand green border, no ring */
    [part='box'][data-filled]:not(:focus-within) {
      border-width: 2px;
      border-color: var(
        --asc-verification-code-input-box-border-focus,
        var(--asc-border-brand)
      );
    }

    /* Focus state — 2px brand border + direct outer ring (no white gap) */
    [part='box']:focus-within {
      border-width: 2px;
      border-color: var(
        --asc-verification-code-input-box-border-focus,
        var(--asc-border-brand)
      );
      box-shadow:
        0px 1px 2px 0px var(--asc-shadow-xs),
        0 0 0 4px var(--asc-verification-code-input-focus-ring, var(--asc-focus-ring));
    }

    /* Destructive state */
    :host([destructive]) [part='box'] {
      border-color: var(
        --asc-verification-code-input-box-border-destructive,
        var(--asc-border-error)
      );
    }

    /* Disabled state */
    :host([disabled]) [part='box'] {
      background: var(
        --asc-verification-code-input-box-bg-disabled,
        var(--asc-bg-disabled_subtle)
      );
      border-color: var(
        --asc-verification-code-input-box-border-disabled,
        var(--asc-border-disabled)
      );
      box-shadow: none;
    }

    /* ── Input element ───────────────────────────────────────── */

    [part='input'] {
      /* Reset native input styles */
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      border: none;
      outline: none;
      margin: 0;
      padding: 0;

      /* Fill the box */
      width: 100%;
      height: 100%;
      cursor: text;

      /* Typography — size sm/md (default) */
      font-family: var(
        --asc-verification-code-input-digit-font-family,
        var(--asc-font-family-display)
      );
      font-weight: var(
        --asc-verification-code-input-digit-font-weight,
        var(--asc-font-weight-medium)
      );
      font-size: var(
        --asc-verification-code-input-digit-font-size-sm,
        var(--asc-font-size-display-lg)
      );
      line-height: var(
        --asc-verification-code-input-digit-line-height-sm,
        var(--asc-line-height-display-lg)
      );
      letter-spacing: -0.96px; /* no-token: display-lg optical tracking, no spacing token match */
      text-align: center;
      color: var(--asc-verification-code-input-digit-color, var(--asc-text-primary));
    }

    :host([size='lg']) [part='input'] {
      font-size: var(
        --asc-verification-code-input-digit-font-size-lg,
        var(--asc-font-size-display-xl)
      );
      line-height: var(
        --asc-verification-code-input-digit-line-height-lg,
        var(--asc-line-height-display-xl)
      );
      letter-spacing: -1.2px; /* no-token: display-xl optical tracking, no spacing token match */
    }

    [part='input']::placeholder {
      color: var(
        --asc-verification-code-input-digit-color-placeholder,
        var(--asc-text-placeholder_subtle)
      );
    }

    :host([disabled]) [part='input'] {
      color: var(
        --asc-verification-code-input-digit-color-disabled,
        var(--asc-text-disabled)
      );
      cursor: not-allowed;
    }

    /* ── Separator ───────────────────────────────────────────── */

    [part='separator'] {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(
        --asc-verification-code-input-separator-font-family,
        var(--asc-font-family-display)
      );
      font-weight: var(--asc-font-weight-medium);
      font-size: var(
        --asc-verification-code-input-separator-font-size,
        var(--asc-font-size-display-lg)
      );
      line-height: var(--asc-line-height-display-lg);
      color: var(
        --asc-verification-code-input-separator-color,
        var(--asc-text-placeholder_subtle)
      );
    }

    :host([size='lg']) [part='separator'] {
      font-size: var(
        --asc-verification-code-input-separator-font-size,
        var(--asc-font-size-display-xl)
      );
      line-height: var(--asc-line-height-display-xl);
    }

    /* ── Hint / error row ────────────────────────────────────── */

    [part='hint-row'] {
      display: block;
      font-size: var(--asc-font-size-text-sm);
      line-height: var(--asc-line-height-text-sm);
      font-family: var(--asc-font-family-body);
      font-weight: var(--asc-font-weight-regular);
    }

    ::slotted([slot='hint']) {
      color: var(--asc-verification-code-input-hint-color, var(--asc-text-tertiary));
    }

    ::slotted([slot='error']) {
      color: var(--asc-verification-code-input-error-color, var(--asc-text-error-primary));
    }

    /* ── Visually hidden utility ─────────────────────────────── */

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
  `;

  // ─── Form association ──────────────────────────────────────

  private readonly _internals: ElementInternals;

  // ─── Unique IDs for ARIA ───────────────────────────────────

  private readonly _uid = generateUid();

  // ─── Reflected properties ──────────────────────────────────

  /** Total number of digit boxes to render. */
  @property({ type: Number, reflect: true }) digits = 6;

  /**
   * Controls the dimensions and font size of the digit boxes.
   * `'sm'` = 64px, `'md'` = 80px, `'lg'` = 96px.
   */
  @property({ type: String, reflect: true }) size: InputSize = 'md';

  /**
   * Restricts accepted characters.
   * `'numeric'` accepts digits only; `'alphanumeric'` accepts letters and digits.
   */
  @property({ type: String, reflect: true }) type: InputType = 'numeric';

  /** When `true`, entered characters are obscured (password-style). */
  @property({ type: Boolean, reflect: true }) masked = false;

  /** When `true`, all boxes are non-interactive and visually muted. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * When `true`, box borders render in the error color and the `error` slot
   * is shown in place of `hint`.
   */
  @property({ type: Boolean, reflect: true }) destructive = false;

  /** Marks the field as required for form semantics. */
  @property({ type: Boolean, reflect: true }) required = false;

  // ─── Non-reflected properties ──────────────────────────────

  /**
   * When > 0, digits are split into groups of this size separated by
   * `separator`. `0` means no grouping.
   */
  @property({ type: Number, attribute: 'group-size' }) groupSize = 0;

  /** Character(s) rendered between groups when `groupSize` is set. */
  @property({ type: String }) separator = '-';

  /**
   * Current code value. Characters are mapped positionally to digit boxes.
   * Setting this property distributes the string across the boxes.
   */
  @property({ type: String })
  get value(): string {
    return this._digitValues.join('');
  }

  set value(v: string) {
    const old = this._digitValues.join('');
    const chars = v.slice(0, this.digits).split('');
    this._digitValues = Array.from({ length: this.digits }, (_, i) => chars[i] ?? '');
    this._internals.setFormValue(this._digitValues.join(''));
    this.requestUpdate('value', old);
  }

  /**
   * When `true`, emits `asc-submit` automatically when the last box is filled.
   */
  @property({ type: Boolean, attribute: 'auto-submit' }) autoSubmit = false;

  /** Name used for form participation (via `ElementInternals`). */
  @property({ type: String }) name = '';

  // ─── Internal state ────────────────────────────────────────

  @state() private _digitValues: string[] = Array(6).fill('');
  @state() private _focusedIndex = -1;
  @state() private _isComplete = false;
  @state() private _liveMessage = '';

  // ─── Blur debounce ────────────────────────────────────────

  private _blurTimeout: ReturnType<typeof setTimeout> | null = null;

  // ─── Constructor ──────────────────────────────────────────

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  // ─── Lifecycle ────────────────────────────────────────────

  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has('digits')) {
      const prev = this._digitValues;
      this._digitValues = Array.from({ length: this.digits }, (_, i) => prev[i] ?? '');
      this._isComplete = this._digitValues.every(v => v !== '');
      this._internals.setFormValue(this._digitValues.join(''));
    }
    if (changed.has('disabled')) {
      this._internals.ariaDisabled = this.disabled ? 'true' : null;
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._blurTimeout !== null) {
      clearTimeout(this._blurTimeout);
      this._blurTimeout = null;
    }
  }

  // ─── Private helpers ──────────────────────────────────────

  private _isValidChar(char: string): boolean {
    if (this.type === 'numeric') return /^\d$/.test(char);
    return /^[a-zA-Z0-9]$/.test(char);
  }

  private _getTabIndex(index: number): number {
    const firstEmpty = this._digitValues.findIndex(v => v === '');
    const tabTarget = firstEmpty === -1 ? this.digits - 1 : firstEmpty;
    return index === tabTarget ? 0 : -1;
  }

  private _focusBox(index: number): void {
    if (index < 0 || index >= this.digits) return;
    const inputs = this.shadowRoot?.querySelectorAll<HTMLInputElement>('[part="input"]');
    inputs?.[index]?.focus();
  }

  private _clearBox(index: number): void {
    if (index < 0 || index >= this.digits) return;
    const newValues = [...this._digitValues];
    newValues[index] = '';
    this._digitValues = newValues;
    this._isComplete = false;
    this._internals.setFormValue(this._digitValues.join(''));
  }

  private _fireAscInput(): void {
    dispatch<{ value: string }>(this, 'asc-input', { detail: { value: this._digitValues.join('') } });
  }

  private _checkComplete(): void {
    const allFilled = this._digitValues.every(v => v !== '');
    if (allFilled && !this._isComplete) {
      this._isComplete = true;
      const val = this._digitValues.join('');
      this._liveMessage = 'Code complete';
      dispatch<{ value: string }>(this, 'asc-complete', { detail: { value: val } });
      if (this.autoSubmit) {
        dispatch<{ value: string }>(this, 'asc-submit', { detail: { value: val } });
      }
    } else if (!allFilled) {
      this._isComplete = false;
    }
  }

  // ─── Event handlers ───────────────────────────────────────

  private _handleKeydown(e: KeyboardEvent, index: number): void {
    const input = e.target as HTMLInputElement;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._focusBox(index - 1);
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._focusBox(index + 1);
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      this._focusBox(0);
      return;
    }

    if (e.key === 'End') {
      e.preventDefault();
      this._focusBox(this.digits - 1);
      return;
    }

    if (e.key === 'Backspace') {
      if (input.value === '') {
        e.preventDefault();
        // Move to previous box and clear it
        const prevIndex = index - 1;
        this._clearBox(prevIndex);
        this._focusBox(prevIndex);
        this._fireAscInput();
      }
      // If box has content, let browser handle the deletion → 'input' event fires
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      this._clearBox(index);
      this._fireAscInput();
      return;
    }

    // Filter printable characters that are not valid
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      if (!this._isValidChar(e.key)) {
        e.preventDefault();
      }
    }
  }

  private _handleInput(e: InputEvent, index: number): void {
    const input = e.target as HTMLInputElement;
    let rawValue = input.value;

    // Safeguard: filter invalid characters (for IME / autofill bypass)
    if (rawValue && !this._isValidChar(rawValue.slice(-1))) {
      rawValue = this._digitValues[index] ?? '';
      input.value = rawValue;
      return;
    }

    // Take only the last character (maxlength=1 normally prevents multiples)
    const newDigit = rawValue ? rawValue.slice(-1) : '';
    const newValues = [...this._digitValues];
    newValues[index] = newDigit;
    this._digitValues = newValues;
    this._internals.setFormValue(this._digitValues.join(''));

    if (newDigit !== '') {
      this._focusBox(index + 1);
    }

    this._fireAscInput();
    this._checkComplete();
  }

  private _handlePaste(e: ClipboardEvent, index: number): void {
    e.preventDefault();
    const text = e.clipboardData?.getData('text') ?? '';
    const filtered = text.split('').filter(c => this._isValidChar(c));

    if (filtered.length === 0) return;

    const newValues = [...this._digitValues];
    let i = index;
    for (const char of filtered) {
      if (i >= this.digits) break;
      newValues[i] = char;
      i++;
    }
    this._digitValues = newValues;
    this._internals.setFormValue(this._digitValues.join(''));

    // Focus next unfilled box after pasted region, or last box if all filled
    const nextEmpty = newValues.findIndex((v, idx) => idx >= i && v === '');
    if (nextEmpty !== -1) {
      this._focusBox(nextEmpty);
    } else {
      const firstEmptyFromStart = newValues.findIndex(v => v === '');
      this._focusBox(firstEmptyFromStart !== -1 ? firstEmptyFromStart : this.digits - 1);
    }

    this._fireAscInput();
    this._checkComplete();
  }

  private _handleFocus(index: number): void {
    if (this._blurTimeout !== null) {
      clearTimeout(this._blurTimeout);
      this._blurTimeout = null;
    }

    this._focusedIndex = index;

    // Select existing character for easy overwriting
    const input = this.shadowRoot?.querySelectorAll<HTMLInputElement>('[part="input"]')[index];
    if (input) {
      input.select();
    }

    dispatch<{ index: number }>(this, 'asc-focus', { detail: { index } });
  }

  private _handleBlur(index: number): void {
    this._blurTimeout = setTimeout(() => {
      this._blurTimeout = null;
      // Only fire asc-blur if no other box received focus in this microtask
      if (this._focusedIndex === index) {
        this._focusedIndex = -1;
        dispatch<{ index: number }>(this, 'asc-blur', { detail: { index } });
      }
    }, 0);
  }

  // ─── Render helpers ───────────────────────────────────────

  private _renderBox(index: number) {
    const inputType = this.masked ? 'password' : 'text';
    const inputMode = this.type === 'numeric' ? 'numeric' : 'text';
    const ariaLabel = `Digit ${index + 1} of ${this.digits}`;
    const tabIndex = this._getTabIndex(index);

    return html`
      <div part="box" ?data-filled=${!!this._digitValues[index]}>
        <input
          part="input"
          .value=${live(this._digitValues[index] ?? '')}
          type=${inputType}
          inputmode=${inputMode}
          maxlength="1"
          tabindex=${tabIndex}
          aria-label=${ariaLabel}
          aria-invalid=${this.destructive ? 'true' : 'false'}
          ?disabled=${this.disabled}
          autocomplete=${index === 0 ? 'one-time-code' : 'off'}
          @keydown=${(e: KeyboardEvent) => this._handleKeydown(e, index)}
          @input=${(e: InputEvent) => this._handleInput(e, index)}
          @paste=${(e: ClipboardEvent) => this._handlePaste(e, index)}
          @focus=${() => this._handleFocus(index)}
          @blur=${() => this._handleBlur(index)}
          placeholder="0"
        />
      </div>
    `;
  }

  private _renderInputRow() {
    const boxes: unknown[] = [];

    for (let i = 0; i < this.digits; i++) {
      boxes.push(this._renderBox(i));

      // Insert separator after every groupSize-th box (except after the last)
      if (this.groupSize > 0 && (i + 1) % this.groupSize === 0 && i < this.digits - 1) {
        boxes.push(html`<span part="separator" aria-hidden="true">${this.separator}</span>`);
      }
    }

    return boxes;
  }

  // ─── Render ───────────────────────────────────────────────

  override render() {
    const labelId = `${this._uid}-label`;
    const hintId = `${this._uid}-hint`;
    const errorId = `${this._uid}-error`;
    const describedBy = this.destructive ? errorId : hintId;

    return html`
      <div part="base">
        <div part="label-row" id=${labelId}>
          <slot name="label"></slot>
        </div>

        <div
          part="input-row"
          role="group"
          aria-labelledby=${labelId}
          aria-describedby=${describedBy}
          aria-disabled=${this.disabled ? 'true' : nothing}
          aria-required=${this.required ? 'true' : nothing}
        >
          ${this._renderInputRow()}
        </div>

        <div part="hint-row">
          ${this.destructive
            ? html`<slot name="error" id=${errorId}></slot>`
            : html`<slot name="hint" id=${hintId}></slot>`}
        </div>

        <!-- Live region announces code completion to assistive technologies -->
        <output class="visually-hidden" aria-live="polite" aria-atomic="true">
          ${this._liveMessage}
        </output>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-verification-code-input': AscVerificationCodeInput;
  }
}
